"use client";

import { useEffect, useState } from "react";
import { ArrowUpRight, Check, Copy, EyeOff, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { SheetShell } from "@/components/odds-screen/live/top-menus";
import { sportsbookIdToDisplay } from "@/lib/sportsbook-registry";
import { slipLink } from "@/lib/fantasy-props/book-links";
import { playerTag } from "@/lib/fantasy-props/player-tag";
import { breakevenPctFromLadder, promoBlocksBoost } from "@/components/optimizer/props-slip-breakevens";
import { legHref, legMultiple, promoOverLimit, slipCap, slipLegsText, slipsStayInOneLeague, type SlipEvaluation, type SlipLegView, type SlipTarget } from "@/lib/fantasy-props/slip";
import type { OddsFormat } from "@/utils/calculators/odds-utils";
import { BestSlipsView, type BestSlipsViewProps } from "./best-slips-view";
import { CrumbMenu, type CrumbItem } from "./crumb-menu";
import { chipClass, SectionLabel, Segmented } from "./chrome";
import { evText } from "./board-parts";
import { slipEvClass } from "@/lib/slip/formatters";
import { formatMultiple, formatOdds, formatPct } from "./model";
import type { LivePricing } from "./use-slip-quote";
import { noStandardText } from "./slip-pricing";
import type { Grading } from "@/lib/fantasy-props/rows";
import { capText, PlayLineText, PromoIcon, PromoTag } from "./promo";

export type SlipView = "best" | "mine";

export type FantasySlipPanelProps = {
  view: SlipView;
  onView: (view: SlipView) => void;
  best: Omit<BestSlipsViewProps, "book" | "grading" | "format" | "isMobile">;
  book: string;
  legs: readonly SlipLegView[];
  target: SlipTarget | null;
  targets: readonly SlipTarget[];
  lockedLeague: string | null;
  onTarget: (id: string) => void;
  evaluation: SlipEvaluation | null;
  live?: LivePricing | null;
  grading: Grading;
  format: OddsFormat;
  onRemove: (rowId: string) => void;
  onHide?: (rowIds: readonly string[]) => void;
  onClear: () => void;
  onClose: () => void;
  isMobile: boolean;
};

const PRIMARY = "tap flex items-center justify-center gap-1.5 rounded-md bg-primary px-4 text-[13px] font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";
const LEG_ACTION = "flex flex-shrink-0 items-center justify-center rounded-md text-zinc-500 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";
const QUIET_TEXT = "rounded-md px-2 text-[12px] font-medium text-zinc-400 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40";

function CopyLegs({ text, tall, primary }: { text: string; tall: boolean; primary: boolean }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1200);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <button
      type="button"
      onClick={() => { void navigator.clipboard.writeText(text).then(() => setCopied(true)).catch(() => {}); }}
      className={primary ? cn(PRIMARY, "flex-1", tall ? "h-11" : "h-8") : cn(chipClass(false, tall), "px-3")}
      aria-label={copied ? "Copied legs" : "Copy legs"}
    >
      {copied ? <Check size={12} strokeWidth={2.5} aria-hidden="true" className={primary ? undefined : "text-primary"} /> : <Copy size={12} strokeWidth={2} aria-hidden="true" />}
      {copied ? "Copied" : "Copy legs"}
    </button>
  );
}

export function FantasySlipPanel({
  view, onView, best, book, legs, target, targets, lockedLeague, onTarget, evaluation, live = null, grading, format, onRemove, onHide, onClear, onClose, isMobile,
}: FantasySlipPanelProps) {
  const tall = isMobile;
  const graded = grading.kind === "graded";
  const bookName = sportsbookIdToDisplay(book);
  const filled = legs.length;
  const total = target?.legs ?? filled;
  const remaining = Math.max(0, total - filled);
  const complete = target !== null && filled >= total && total > 0;
  const moved = legs.find((leg) => leg.moved && leg.current !== null);
  const gone = legs.find((leg) => leg.gone);
  const liveIds = legs.filter((leg) => leg.current !== null).map((leg) => leg.rowId);
  const boardLoading = legs.some((leg) => leg.current === null && !leg.gone);
  const room = evaluation?.roomPct ?? null;
  const evPct = evaluation?.evPct ?? null;
  const plusEv = evPct !== null && evPct > 0;
  const legLinks = legs.map((leg) => legHref(leg, isMobile));
  const anyLink = legLinks.some((link) => link !== null);
  const placeHref = (complete ? slipLink(book, legLinks) : null) ?? legLinks.find((link) => link !== null) ?? null;
  const title = view === "best" ? "+EV Slips" : "Slip";
  const payout = evaluation?.payout ?? target?.payout ?? 0;
  const boostOff = target !== null && target.boostPct > 0 && promoBlocksBoost(target.platform) && legs.some((leg) => leg.promo !== null);
  const cut = live?.status === "priced" && live.factor < 1 ? live : null;
  const liveMeta = !graded ? noStandardText(bookName)
    : live === null ? null
    : live.status === "pricing" ? `checking ${bookName} correlation`
    : live.status !== "priced" ? `${bookName} correlation unavailable`
    : `${live.factor < 1 ? `${bookName} correlation ×${Math.round(live.factor * 1000) / 1000}` : `no ${bookName} correlation cut`}${live.stale ? " · last check" : ""}`;
  const liveNote = cut !== null
    ? `Some of these picks are correlated, so ${bookName} pays ${formatMultiple(payout)} instead of ${formatMultiple(cut.uncutPayout)}.`
    : live?.status === "refused" ? `${bookName} won't take these picks together: ${live.detail}`
    : live?.status !== "unavailable" ? null
    : live.reason === "unreachable" ? `${bookName} isn't answering, so this payout has no correlation cut yet.`
    : live.reason === "mixed" ? null
    : live.reason === "unreadable" ? `A leg is missing its ${bookName} pick id, so this payout has no correlation cut.`
    : `${bookName} doesn't list a ${total} pick payout on this slate, so this is Upside's table with no correlation cut.`;
  const oneSlate = slipsStayInOneLeague(book);
  const promoLimit = promoOverLimit(legs);
  const notes = [
    oneSlate && new Set(legs.map((leg) => leg.scope ?? leg.league)).size > 1 ? `These picks come from different ${bookName} slates, so ${bookName} can't price them or take them as one entry.` : null,
    promoLimit !== null ? `${bookName} allows ${promoLimit === 1 ? "one promo pick" : `${promoLimit} promo picks`} per slip, so this slip can't be entered as is.` : null,
  ].filter((note): note is string => note !== null);
  const promoTag = legs.some((leg) => leg.promo !== null) ? (capText(slipCap(legs)) === null ? "Promo" : `Promo · ${capText(slipCap(legs))}`) : null;

  const targetItems: CrumbItem[] = targets.map((option) => ({
    id: option.id, label: option.label, meta: graded ? formatMultiple(option.payout) : undefined, active: option.id === target?.id,
  }));

  const switcher = (
    <div className={cn("flex-shrink-0 border-b border-surface-line px-3", tall ? "py-2.5" : "py-2")}>
      <Segmented<SlipView>
        label="Slip view"
        value={view}
        onChange={onView}
        tall={tall}
        stretch
        options={[
          { id: "best", label: "Best slips" },
          { id: "mine", label: filled > 0 ? <>My slip<span className="data-num text-[11px] text-primary">{filled}</span></> : "My slip", ariaLabel: filled > 0 ? `My slip, ${filled} ${filled === 1 ? "leg" : "legs"}` : "My slip" },
        ]}
      />
    </div>
  );

  const header = (
    <div className="flex h-11 flex-shrink-0 items-center gap-2 border-b border-surface-line pl-4 pr-2">
      <span className="text-[15px] font-semibold text-foreground">{title}</span>
      <span className="flex-grow" />
      <button type="button" onClick={onClose} aria-label="Close" className="flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 hover:bg-white/[0.06] hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
        <X size={14} strokeWidth={2.5} aria-hidden="true" />
      </button>
    </div>
  );

  const mine = (
    <div className="px-2">
      <SectionLabel>Slip type</SectionLabel>
      {target === null ? (
        <p className="px-1.5 text-[12px] text-zinc-500">{filled > 0 ? `${bookName} has no ${filled} pick slip here.` : `${bookName} has no payout table here yet.`}</p>
      ) : (
        <div className="flex flex-col gap-1 px-1.5">
          <CrumbMenu title="Slip type" label={target.label} items={targetItems} isMobile={isMobile} tone="quiet" onPick={onTarget} className="-ml-1.5 min-w-0 self-start" />
          <div className="data-num flex items-center gap-2 text-[12px] text-zinc-500">
            {graded && (
              <span className="min-w-0">
                <span className="font-semibold text-zinc-300">{formatMultiple(payout)}</span>
                {cut !== null && <s className="ml-1 text-zinc-500">{formatMultiple(cut.uncutPayout)}</s>}
                {target.boostPct > 0 && (boostOff ? <> · no boost with a promo pick</> : <> · {formatMultiple(target.basePayout)} with {target.boostPct}% boost</>)}
              </span>
            )}
            <span className="ml-auto flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap">
              {lockedLeague !== null && <span className="rounded bg-white/[0.06] px-1 text-[10px] font-semibold leading-4 tracking-[0.04em] text-zinc-400">{lockedLeague} only</span>}
              {promoTag !== null && <PromoTag>{promoTag}</PromoTag>}
            </span>
          </div>
          {liveMeta !== null && <span className="data-num text-[11px] leading-4 text-zinc-500">{liveMeta}</span>}
        </div>
      )}

      <div className="flex items-center justify-between pr-1.5 pt-3">
        <SectionLabel className="pt-0">
          Legs
          {target !== null && <span className={cn("ml-1.5 normal-case tracking-normal", complete ? "text-accent-value" : "text-primary")}>{filled} of {total}</span>}
        </SectionLabel>
        {filled > 0 && (
          <span className="flex items-center">
            {onHide !== undefined && (liveIds.length > 0 ? (
              <button type="button" onClick={() => onHide(liveIds)} className={cn(QUIET_TEXT, "flex items-center gap-1", tall ? "h-11" : "h-7")}>
                <EyeOff size={12} aria-hidden="true" />
                Hide all
              </button>
            ) : (
              <span aria-hidden="true" className={cn(QUIET_TEXT, "invisible flex items-center gap-1", tall ? "h-11" : "h-7")}>
                <EyeOff size={12} />
                Hide all
              </span>
            ))}
            <button type="button" onClick={onClear} className={cn(QUIET_TEXT, tall ? "h-11" : "h-7")}>Clear</button>
          </span>
        )}
      </div>
      <ul role="list" aria-label="Legs" className="flex flex-col">
        {legs.map((leg) => {
          const hit = leg.current?.hit ?? leg.hit;
          const odds = leg.current?.odds ?? leg.odds;
          const power = leg.promo?.kind === "power" ? legMultiple(leg) : null;
          const tag = playerTag({ player_position: leg.position, team_short: leg.team });
          const loading = leg.current === null && !leg.gone;
          return (
            <li key={leg.rowId} aria-busy={loading || undefined} className={cn("flex items-start gap-2.5 rounded-md py-2 pl-2.5 pr-1", leg.moved && "animate-in fade-in-0 duration-300 motion-reduce:animate-none")}>
              <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border border-primary bg-primary/20 text-primary" aria-hidden="true">
                <Check size={10} strokeWidth={3} />
              </span>
              <span className="flex min-w-0 flex-grow flex-col">
                <span className="flex items-center gap-1.5 text-[13px] font-semibold leading-[18px] text-foreground"><span className="truncate">{leg.player}</span>{tag !== null && <span className="flex-shrink-0 text-[11px] font-medium leading-[18px] text-zinc-500">{tag}</span>}{leg.promo !== null && <PromoIcon promo={leg.promo} />}</span>
                <span className="text-[12px] leading-4 text-zinc-400">
                  {leg.gone ? <span className="text-destructive">No longer offered</span> : leg.current !== null && leg.lineMoved ? <>Line moved · now {leg.play.side} {leg.current.line ?? ""} · {leg.play.market}</> : <PlayLineText play={leg.play} />}
                </span>
                {leg.moved && leg.current !== null ? (
                  <span className="data-num mt-0.5 flex items-center gap-1 text-[11px] text-zinc-500">
                    <span className="size-1.5 rounded-full bg-warning" aria-hidden="true" />
                    was {formatPct(leg.hit)}{leg.odds !== null && <> · {formatOdds(leg.odds, format)}</>}
                  </span>
                ) : loading && tall && <span aria-hidden="true" className="data-num mt-0.5 flex items-center gap-1 text-[11px]">{"\u00a0"}</span>}
              </span>
              <span className="flex flex-shrink-0 flex-col items-end gap-0.5">
                <span className={cn("data-num inline-flex h-6 items-center rounded-md border px-1.5 text-[12px] font-semibold", leg.current === null || !graded ? "border-white/10 bg-white/5 text-zinc-500" : "border-accent-value/30 bg-accent-value/20 text-accent-value", loading && "animate-pulse motion-reduce:animate-none")}>{formatPct(hit)}</span>
                {odds !== null && (
                  <span className="flex items-center gap-1">
                    <span className="data-num text-[12px] font-semibold text-zinc-400">{formatOdds(odds, format)}</span>
                    {power !== null && <PromoTag>{formatMultiple(power)}</PromoTag>}
                  </span>
                )}
              </span>
              {onHide !== undefined && (leg.current !== null ? (
                <button type="button" onClick={() => onHide([leg.rowId])} aria-label={`Hide ${leg.player}`} className={cn(LEG_ACTION, tall ? "h-11 w-11" : "h-7 w-7")}>
                  <EyeOff size={12} aria-hidden="true" />
                </button>
              ) : <span aria-hidden="true" className={cn("flex-shrink-0", tall ? "h-11 w-11" : "h-7 w-7")} />)}
              <button type="button" onClick={() => onRemove(leg.rowId)} aria-label={`Remove ${leg.player} from slip`} className={cn(LEG_ACTION, tall ? "h-11 w-11" : "h-7 w-7")}>
                <X size={12} strokeWidth={2.5} aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
      {notes.map((note) => (
        <p key={note} className="flex items-center gap-2 px-1.5 pb-1 pt-1 text-[12px] text-zinc-400" role="note"><span className="size-1.5 flex-shrink-0 rounded-full bg-warning" aria-hidden="true" />{note}</p>
      ))}
      {target !== null && remaining > 0 && graded && (
        <div className="mx-1.5 mt-1 flex items-center gap-2.5 rounded-md border border-dashed border-primary/40 bg-primary/[0.04] px-2.5 py-2.5" role="status">
          <span className="h-4 w-4 flex-shrink-0 rounded border border-dashed border-primary" aria-hidden="true" />
          <span className="flex min-w-0 flex-col">
            {filled === 0 ? (
              <>
                <span className="text-[13px] font-semibold leading-[18px] text-primary">Add {total} plays from the board</span>
                <span className="text-[12px] leading-4 text-zinc-400">Each needs {formatPct(breakevenPctFromLadder(target.ladder))} or better at {formatMultiple(payout)}.</span>
              </>
            ) : evaluation?.hopeless ? (
              <>
                <span className="text-[13px] font-semibold leading-[18px] text-warning">No leg can make this slip +EV</span>
                <span className="text-[12px] leading-4 text-zinc-400">The legs in already need more than {formatMultiple(payout)} pays. Swap one out.</span>
              </>
            ) : (
              <>
                <span className="data-num text-[13px] font-semibold leading-[18px] text-primary">
                  {remaining === 1 ? `Leg ${filled + 1} · any ${lockedLeague ? `${lockedLeague} ` : ""}play at ${room === null ? "–" : formatPct(room)} or better` : `${remaining} more legs · each at ${room === null ? "–" : formatPct(room)} or better`}
                </span>
                <span className="text-[12px] leading-4 text-zinc-400">keeps this slip +EV at {formatMultiple(payout)}.</span>
              </>
            )}
          </span>
        </div>
      )}

      {target !== null && filled > 0 && evaluation !== null && (
        <>
          <SectionLabel className="pt-3.5">{complete ? "Slip" : "Slip so far"}</SectionLabel>
          {gone !== undefined && (
            <p className="flex items-center gap-2 px-1.5 pb-1 text-[12px] text-zinc-400"><span className="size-1.5 flex-shrink-0 rounded-full bg-destructive" aria-hidden="true" />{gone.player} left the board. Remove it or pick another play.</p>
          )}
          {moved !== undefined && moved.current !== null && (
            <p className="flex items-center gap-2 px-1.5 pb-1 text-[12px] text-zinc-400">
              <span className="size-1.5 flex-shrink-0 rounded-full bg-warning" aria-hidden="true" />
              <span>{moved.player} moved to {moved.current.odds === null ? formatPct(moved.current.hit) : formatOdds(moved.current.odds, format)}.
              {complete ? (plusEv ? " Still +EV." : " No longer +EV.") : evaluation.hopeless ? " The slip can no longer be +EV." : room === null ? "" : ` Room for leg ${filled + 1} is now ${formatPct(room)}.`}</span>
            </p>
          )}
          {liveNote !== null && (
            <p className="flex items-center gap-2 px-1.5 pb-1 text-[12px] text-zinc-400" role="note"><span className="size-1.5 flex-shrink-0 rounded-full bg-warning" aria-hidden="true" />{liveNote}</p>
          )}
          {boardLoading && gone === undefined && moved === undefined && liveNote === null && <p aria-hidden="true" className="px-1.5 pb-1 text-[12px]">{"\u00a0"}</p>}
          <dl className="flex flex-col px-1.5">
            <div className="flex h-8 items-center justify-between text-[13px]">
              <dt className="text-zinc-300">Hit chance <span className="text-[11px] text-zinc-500">{complete ? "all legs" : `${filled} ${filled === 1 ? "leg" : "legs"} in`}</span></dt>
              <dd className="data-num font-semibold text-foreground">{evaluation.hitAllPct === null ? "–" : formatPct(evaluation.hitAllPct)}</dd>
            </div>
            {evaluation.needAllPct !== null && (
              <div className="flex h-8 items-center justify-between text-[13px]">
                <dt className="text-zinc-300">Needs at {formatMultiple(payout)} <span className="text-[11px] text-zinc-500">all {total}</span></dt>
                <dd className="data-num font-semibold text-foreground">{formatPct(evaluation.needAllPct)}</dd>
              </div>
            )}
            {complete && evaluation.parlayOdds !== null && (
              <div className="flex h-8 items-center justify-between text-[13px]">
                <dt className="text-zinc-300">Parlay odds</dt>
                <dd className="data-num font-semibold text-foreground">{formatOdds(evaluation.parlayOdds, format)}</dd>
              </div>
            )}
            {!complete && (
              <div className="flex h-8 items-center justify-between text-[13px]">
                <dt className="text-zinc-300">Room for {remaining === 1 ? `leg ${filled + 1}` : "each leg left"}</dt>
                <dd className="data-num font-semibold text-primary">{room === null ? "–" : `${formatPct(room)}+`}</dd>
              </div>
            )}
            {complete && evPct !== null && (
              <div className="mt-1 flex h-9 items-center justify-between border-t border-surface-line text-[13px]">
                <dt className="text-zinc-300">Slip EV</dt>
                <dd className={cn("data-num inline-flex h-6 items-center rounded-md border px-2 text-[13px] font-semibold", slipEvClass(evPct))}>{evText(evPct)}</dd>
              </div>
            )}
          </dl>
        </>
      )}
    </div>
  );

  const body = (
    <div className="min-h-0 flex-grow overflow-y-auto pb-2 scrollbar-custom scrollbar-color-primary">
      {view === "best" ? <BestSlipsView {...best} book={book} grading={grading} format={format} isMobile={isMobile} /> : mine}
    </div>
  );

  const footer = view === "mine" ? (
    <div className={cn("flex flex-shrink-0 items-center gap-2 border-t border-surface-line px-3", tall ? "py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" : "h-14")}>
      <CopyLegs text={slipLegsText(legs, bookName, graded ? target : null, payout)} tall={tall} primary={!anyLink} />
      {anyLink && (complete && placeHref !== null ? (
        <a href={placeHref} target="_blank" rel="noopener noreferrer" className={cn(PRIMARY, "flex-1", tall ? "h-11" : "h-8")}>
          Place on {bookName}
          <ArrowUpRight size={12} strokeWidth={2.5} aria-hidden="true" />
        </a>
      ) : (
        <button type="button" disabled className={cn("flex flex-1 items-center justify-center rounded-md bg-primary/35 px-4 text-[13px] font-semibold text-surface-base/80", tall ? "h-11" : "h-8")}>
          {complete ? `Open ${bookName}` : target === null ? "Pick a slip type" : `Place on ${bookName} · pick leg ${filled + 1}`}
        </button>
      ))}
    </div>
  ) : null;

  if (isMobile) {
    return (
      <SheetShell open title={title} onClose={onClose}>
        {switcher}
        {body}
        {footer}
      </SheetShell>
    );
  }

  return (
    <aside aria-label={title} className="flex w-[400px] flex-shrink-0 flex-col border-l border-surface-line bg-surface-raised">
      {header}
      {switcher}
      {body}
      {footer}
    </aside>
  );
}
